import React, { useMemo, useState } from 'react'
import Select from 'react-select'
import { useAllUserPresence } from '../../Context/AllUsersPresenceProvider'

const REP_STATUS_META = {
  online: {
    dot: '#22c55e',
    background: '#dcfce7',
    color: '#166534',
    label: 'Online',
  },
  offline: {
    dot: '#cbd5e1',
    background: '#f1f5f9',
    color: '#64748b',
    label: 'Offline',
  },
}

const formatRepDisplayName = (rep) => {
  if (!rep) return ''
  const fullName = [rep.firstName, rep.lastName].filter(Boolean).join(' ').trim()
  return fullName || rep.name || rep.username || 'Unknown rep'
}

const normalizeRepOption = (rep, presenceMap, getSecondaryLabel) => {
  if (!rep?.id) return null
  return {
    value: rep.id,
    label: formatRepDisplayName(rep),
    secondaryLabel: getSecondaryLabel ? getSecondaryLabel(rep) : (rep.email || (rep.username ? `@${rep.username}` : '')),
    status: presenceMap?.has(rep.id) ? 'online' : 'offline',
    rep,
  }
}

const compareRepOptions = (left, right) => {
  if (left.status !== right.status) {
    return left.status === 'online' ? -1 : 1
  }
  return left.label.localeCompare(right.label)
}

const renderRepOptionLabel = (option, { context, showStatus }) => {
  const statusMeta = REP_STATUS_META[option.status] || REP_STATUS_META.offline

  if (context === 'value') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {showStatus ? (
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: statusMeta.dot,
              flexShrink: 0,
            }}
          />
        ) : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {option.label}
        </span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
        <div style={{ color: '#0f172a', fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {option.label}
        </div>
        {option.secondaryLabel ? (
          <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {option.secondaryLabel}
          </div>
        ) : null}
      </div>
      {showStatus ? (
        <span
          style={{
            alignItems: 'center',
            backgroundColor: statusMeta.background,
            borderRadius: 999,
            color: statusMeta.color,
            display: 'inline-flex',
            flexShrink: 0,
            fontSize: 11,
            fontWeight: 600,
            gap: 6,
            padding: '4px 8px',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: statusMeta.dot,
            }}
          />
          {statusMeta.label}
        </span>
      ) : null}
    </div>
  )
}

const buildSelectStyles = (minMenuWidth, maxMenuWidth) => ({
  control: (base, state) => ({
    ...base,
    backgroundColor: state.isDisabled ? '#f1f5f9' : '#ffffff',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    borderRadius: 12,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
    minHeight: 40,
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: '#2563eb',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 12px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#64748b',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#1f2937',
    margin: 0,
    maxWidth: '100%',
  }),
  input: (base) => ({
    ...base,
    color: '#1f2937',
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#6b7280',
    padding: 8,
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 8,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.18)',
    overflow: 'hidden',
    minWidth: minMenuWidth,
    width: 'max-content',
    maxWidth: maxMenuWidth,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#eff6ff' : state.isFocused ? '#f8fafc' : '#ffffff',
    color: '#0f172a',
    cursor: 'pointer',
    padding: '10px 12px',
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: '#64748b',
    fontSize: 12,
  }),
  loadingMessage: (base) => ({
    ...base,
    color: '#64748b',
    fontSize: 12,
  }),
})

const defaultNoOptionsMessage = ({ inputValue }) => (
  inputValue?.trim() ? 'No reps found for this search.' : 'No reps available.'
)

const defaultLoadingMessage = () => 'Loading reps...'

export default function RepTypeAhead({
  reps = [],
  value = null,
  onChange,
  inputValue,
  onInputChange,
  onMenuClose,
  isLoading = false,
  isDisabled = false,
  placeholder = 'Search reps...',
  noOptionsMessage = defaultNoOptionsMessage,
  loadingMessage = defaultLoadingMessage,
  isClearable = false,
  menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
  menuPosition = 'fixed',
  disableLocalFilter = false,
  showStatus = true,
  getSecondaryLabel,
  minMenuWidth = 280,
  maxMenuWidth = 'min(420px, calc(100vw - 24px))',
}) {
  const allUserPresence = useAllUserPresence()
  const [internalInputValue, setInternalInputValue] = useState('')

  const options = useMemo(() => {
    const seen = new Set()
    return (reps || [])
      .map((rep) => normalizeRepOption(rep, allUserPresence, getSecondaryLabel))
      .filter((option) => {
        if (!option?.value || seen.has(option.value)) return false
        seen.add(option.value)
        return true
      })
      .sort(compareRepOptions)
  }, [allUserPresence, getSecondaryLabel, reps])

  const selectedOption = useMemo(() => {
    if (!value) return null

    const selectedId = typeof value === 'string' ? value : (value?.value || value?.id || value?.rep?.id)
    if (!selectedId) return null

    return options.find((option) => option.value === selectedId)
      || normalizeRepOption(value?.rep || value, allUserPresence, getSecondaryLabel)
  }, [allUserPresence, getSecondaryLabel, options, value])

  const resolvedInputValue = inputValue !== undefined ? inputValue : internalInputValue

  const handleInputChange = (nextInputValue, meta) => {
    if (meta.action !== 'input-change') {
      return nextInputValue
    }

    if (inputValue === undefined) {
      setInternalInputValue(nextInputValue)
    }
    onInputChange && onInputChange(nextInputValue, meta)
    return nextInputValue
  }

  const handleMenuClose = () => {
    if (inputValue === undefined) {
      setInternalInputValue('')
    }
    onMenuClose && onMenuClose()
  }

  const filterOption = disableLocalFilter ? (() => true) : ((candidate, rawInput) => {
    const normalized = rawInput.trim().toLowerCase()
    if (!normalized) return true
    const label = candidate.label?.toLowerCase() || ''
    const secondaryLabel = candidate.data?.secondaryLabel?.toLowerCase() || ''
    return label.includes(normalized) || secondaryLabel.includes(normalized)
  })

  return (
    <Select
      options={options}
      value={selectedOption}
      onChange={onChange}
      onInputChange={handleInputChange}
      inputValue={resolvedInputValue}
      onMenuClose={handleMenuClose}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isSearchable
      placeholder={placeholder}
      noOptionsMessage={noOptionsMessage}
      loadingMessage={loadingMessage}
      filterOption={filterOption}
      formatOptionLabel={(option, meta) => renderRepOptionLabel(option, { ...meta, showStatus })}
      styles={buildSelectStyles(minMenuWidth, maxMenuWidth)}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
    />
  )
}
